import React, { useState, useEffect, useCallback } from 'react';
import { 
  Plus, 
  Edit, 
  Trash2, 
  Send, 
  Mail, 
  Calendar, 
  Clock, 
  CheckCircle, 
  XCircle,
  BarChart3,
  Users,
  Search,
  Filter
} from 'lucide-react';
import { useToastContext } from '../context/ToastContext';
import { useEvent } from '../context/EventContext';
import Layout from '../components/layout/Layout';
import EmailComposer from '../components/email/EmailComposer';
import Loading from '../components/common/Loading';
import { eventAPI, attendeeAPI } from '../api/endpoints';
import { getApiUrl } from '../utils/apiConfig';

const EmailCampaigns = () => {
  const toast = useToastContext();
  const { selectedEvent, setSelectedEvent } = useEvent();
  const [campaigns, setCampaigns] = useState([]);
  const [attendees, setAttendees] = useState([]);
  const [events, setEvents] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showComposer, setShowComposer] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showStats, setShowStats] = useState({});

  // Define functions before using them in useEffect
  const loadEvents = useCallback(async () => {
    try {
      console.log('📧 Loading events for email campaigns...');
      const response = await eventAPI.getAll();
      console.log('📧 Events response:', response);
      
      const eventsList = response.data?.events || response.data || [];
      setEvents(eventsList);
      
      if (eventsList.length > 0) {
        setSelectedEvent(eventsList[0].id);
        console.log('📧 Selected first event:', eventsList[0].id);
      } else {
        console.log('📧 No events available');
      }
    } catch (error) {
      console.error('❌ Failed to load events:', error);
      toast.error('Failed to load events');
    }
  }, [toast, setSelectedEvent]);

  const loadCampaigns = useCallback(async () => {
    if (!selectedEvent) return;

    setIsLoading(true);
    try {
      const url = getApiUrl(`events/${selectedEvent}/email-campaigns`);
      console.log('📧 Loading campaigns from:', url);
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Accept': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        setCampaigns(data.data?.data || []);
        console.log('📧 Campaigns loaded:', data.data?.data?.length || 0);
      } else {
        console.error('❌ Failed to load campaigns:', response.status);
        toast.error('Failed to load email campaigns');
      }
    } catch (error) {
      console.error('❌ Failed to load campaigns:', error);
      toast.error('Failed to load email campaigns');
    } finally {
      setIsLoading(false);
    }
  }, [selectedEvent, toast]);

  const loadAttendees = useCallback(async () => {
    if (!selectedEvent) return;

    try {
      console.log('📧 Loading attendees for event:', selectedEvent);
      const response = await attendeeAPI.getAll(selectedEvent);
      console.log('📧 Attendees response:', response);
      
      const attendeesList = response.data?.attendees || response.data || [];
      setAttendees(attendeesList);
      console.log('📧 Attendees loaded:', attendeesList.length);
    } catch (error) {
      console.error('❌ Failed to load attendees:', error);
    }
  }, [selectedEvent]);

  const handleDeleteCampaign = async (campaignId) => {
    const confirmed = window.confirm('Are you sure you want to delete this campaign?');
    if (!confirmed) return;

    try {
      const url = getApiUrl(`events/${selectedEvent}/email-campaigns/${campaignId}`);
      const response = await fetch(url, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Accept': 'application/json',
        },
      });

      if (response.ok) {
        toast.success('Campaign deleted successfully');
        loadCampaigns();
      } else {
        toast.error('Failed to delete campaign');
      }
    } catch (error) {
      console.error('Failed to delete campaign:', error);
      toast.error('Failed to delete campaign');
    }
  };

  const handleSendCampaign = async (campaignId) => {
    const confirmed = window.confirm('Are you sure you want to send this campaign?');
    if (!confirmed) return;

    try {
      const url = getApiUrl(`events/${selectedEvent}/email-campaigns/${campaignId}/send`);
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Accept': 'application/json',
        },
      });

      if (response.ok) {
        toast.success('Campaign is being sent');
        loadCampaigns();
      } else {
        toast.error('Failed to send campaign');
      }
    } catch (error) {
      console.error('Failed to send campaign:', error);
      toast.error('Failed to send campaign');
    }
  };

  const loadCampaignStats = async (campaignId) => {
    try {
      const url = getApiUrl(`events/${selectedEvent}/email-campaigns/${campaignId}/stats`);
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Accept': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        setShowStats(prev => ({
          ...prev,
          [campaignId]: data.data
        }));
      }
    } catch (error) {
      console.error('Failed to load campaign stats:', error);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'draft':
        return <Edit className="w-4 h-4 text-gray-500" />;
      case 'scheduled':
        return <Clock className="w-4 h-4 text-blue-500" />;
      case 'sending':
        return <Send className="w-4 h-4 text-yellow-500" />;
      case 'sent':
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'failed':
        return <XCircle className="w-4 h-4 text-red-500" />;
      default:
        return <Mail className="w-4 h-4 text-gray-500" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'draft':
        return 'bg-gray-100 text-gray-800';
      case 'scheduled':
        return 'bg-blue-100 text-blue-800';
      case 'sending':
        return 'bg-yellow-100 text-yellow-800';
      case 'sent':
        return 'bg-green-100 text-green-800';
      case 'failed':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // useEffect hooks after function definitions
  useEffect(() => {
    loadEvents();
  }, [loadEvents]);

  useEffect(() => {
    if (selectedEvent) {
      loadCampaigns();
      loadAttendees();
    }
  }, [selectedEvent, loadCampaigns, loadAttendees]);

  const filteredCampaigns = campaigns.filter(campaign => {
    const matchesSearch = campaign.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         campaign.subject.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || campaign.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  if (!selectedEvent) {
    return (
      <Layout>
        <div className="p-6">
          <div className="text-center">
            <Mail className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No event selected</h3>
            <p className="mt-1 text-sm text-gray-500">
              Please select an event to view and manage email campaigns.
            </p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Email Campaigns</h1>
            <p className="text-gray-600">Create and manage email campaigns for your events</p>
          </div>
          <button
            onClick={() => setShowComposer(true)}
            className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            New Campaign
          </button>
        </div>

        {/* Event Selector */}
        <div className="mt-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Select Event
          </label>
          <select
            value={selectedEvent}
            onChange={(e) => setSelectedEvent(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
          >
            <option value="">Select an event</option>
            {events.map(event => (
              <option key={event.id} value={event.id}>
                {event.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Filters */}
      <div className="mb-6 flex items-center gap-4">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          <input
            type="text"
            placeholder="Search campaigns..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
          />
        </div>
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-gray-500" />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
          >
            <option value="all">All Status</option>
            <option value="draft">Draft</option>
            <option value="scheduled">Scheduled</option>
            <option value="sending">Sending</option>
            <option value="sent">Sent</option>
            <option value="failed">Failed</option>
          </select>
        </div>
      </div>

      {/* Campaigns List */}
      {isLoading ? (
        <div className="flex justify-center py-8">
          <Loading />
        </div>
      ) : filteredCampaigns.length === 0 ? (
        <div className="text-center py-8">
          <Mail className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No campaigns found</h3>
          <p className="mt-1 text-sm text-gray-500">
            {campaigns.length === 0 
              ? "Get started by creating your first email campaign."
              : "Try adjusting your search or filter criteria."
            }
          </p>
          {campaigns.length === 0 && (
            <button
              onClick={() => setShowComposer(true)}
              className="mt-4 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors"
            >
              Create Campaign
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          {filteredCampaigns.map(campaign => (
            <div key={campaign.id} className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    {getStatusIcon(campaign.status)}
                    <h3 className="text-lg font-semibold text-gray-900">{campaign.name}</h3>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(campaign.status)}`}>
                      {campaign.status.charAt(0).toUpperCase() + campaign.status.slice(1)}
                    </span>
                  </div>
                  
                  <p className="text-gray-600 mb-3">{campaign.subject}</p>
                  
                  <div className="flex items-center gap-6 text-sm text-gray-500">
                    <div className="flex items-center gap-1">
                      <Users className="w-4 h-4" />
                      <span>{campaign.total_recipients} recipients</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Calendar className="w-4 h-4" />
                      <span>{new Date(campaign.created_at).toLocaleDateString()}</span>
                    </div>
                    {campaign.sent_at && (
                      <div className="flex items-center gap-1">
                        <Send className="w-4 h-4" />
                        <span>Sent {new Date(campaign.sent_at).toLocaleDateString()}</span>
                      </div>
                    )}
                  </div>

                  {/* Stats */}
                  {campaign.status === 'sent' && (
                    <div className="mt-4 grid grid-cols-3 gap-4">
                      <div className="text-center p-3 bg-green-50 rounded-lg">
                        <div className="text-lg font-semibold text-green-600">{campaign.delivery_rate}%</div>
                        <div className="text-xs text-green-700">Delivered</div>
                      </div>
                      <div className="text-center p-3 bg-blue-50 rounded-lg">
                        <div className="text-lg font-semibold text-blue-600">{campaign.open_rate}%</div>
                        <div className="text-xs text-blue-700">Opened</div>
                      </div>
                      <div className="text-center p-3 bg-purple-50 rounded-lg">
                        <div className="text-lg font-semibold text-purple-600">{campaign.click_rate}%</div>
                        <div className="text-xs text-purple-700">Clicked</div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2 ml-4">
                  {campaign.status === 'sent' && (
                    <button
                      onClick={() => {
                        if (showStats[campaign.id]) {
                          setShowStats(prev => ({ ...prev, [campaign.id]: null }));
                        } else {
                          loadCampaignStats(campaign.id);
                        }
                      }}
                      className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                      title="View Stats"
                    >
                      <BarChart3 className="w-4 h-4" />
                    </button>
                  )}
                  
                  {campaign.status === 'draft' && (
                    <>
                      <button
                        onClick={() => {
                          setEditingCampaign(campaign);
                          setShowComposer(true);
                        }}
                        className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                        title="Edit"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleSendCampaign(campaign.id)}
                        className="p-2 text-teal-600 hover:text-teal-700 hover:bg-teal-50 rounded-lg transition-colors"
                        title="Send Campaign"
                      >
                        <Send className="w-4 h-4" />
                      </button>
                    </>
                  )}
                  
                  {campaign.status !== 'sending' && (
                    <button
                      onClick={() => handleDeleteCampaign(campaign.id)}
                      className="p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
                      title="Delete"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>

              {/* Detailed Stats */}
              {showStats[campaign.id] && (
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <h4 className="text-sm font-medium text-gray-900 mb-3">Detailed Statistics</h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div>
                      <div className="text-gray-500">Total Sent</div>
                      <div className="font-semibold">{showStats[campaign.id].sent_count}</div>
                    </div>
                    <div>
                      <div className="text-gray-500">Failed</div>
                      <div className="font-semibold text-red-600">{showStats[campaign.id].failed_count}</div>
                    </div>
                    <div>
                      <div className="text-gray-500">Opened</div>
                      <div className="font-semibold text-blue-600">{showStats[campaign.id].opened_count}</div>
                    </div>
                    <div>
                      <div className="text-gray-500">Clicked</div>
                      <div className="font-semibold text-purple-600">{showStats[campaign.id].clicked_count}</div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Email Composer Modal */}
      {showComposer && (
        <EmailComposer
          isOpen={showComposer}
          onClose={() => {
            setShowComposer(false);
            setEditingCampaign(null);
          }}
          eventId={selectedEvent}
          attendees={attendees}
          initialData={editingCampaign}
          onSendComplete={() => {
            loadCampaigns();
            setEditingCampaign(null);
          }}
        />
      )}
      </div>
    </Layout>
  );
};

export default EmailCampaigns;
